import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, FileSignature, MessageSquare, ArrowRight } from 'lucide-react';
import { getReviewHistory } from '../../services/reviewWorkflowApi';
import {
  SUBMIT_META,
  CURRENT_STATUS_META,
  VERSION_TOOLTIP,
  fmt,
  deciderOf,
  prepareCycles,
} from './reviewHistoryShared';

/**
 * At-a-glance review/attestation history for one reviewable entity (FECOA-1611).
 *
 * Deliberately simplified: one compact row per event (a submission, then its
 * decision) so each record fits on a single line and the whole story scans top
 * to bottom without scrolling sideways. The rich, per-event detail — decision
 * reasons and the full 21 CFR Part 11 e-signature block — lives on the
 * dedicated audit page reached via "View full audit history".
 */

// Small colored pill mirroring the event's meta. Kept inline so the row stays
// on one line at typical widths.
const EventPill = ({ meta }) => {
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${meta.cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {meta.label}
    </span>
  );
};

// The "Details" cell: for a submission we surface the reviewed-version state;
// for a decision we surface muted markers that a reason and/or signature exist
// (the full text is on the audit page).
const versionCell = (version) => {
  if (version.kind === 'none') return null;
  if (version.kind === 'unchanged') {
    return (
      <span className="text-[11px] italic text-gray-400 dark:text-gray-500" title={VERSION_TOOLTIP}>
        Unchanged
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 cursor-help" title={VERSION_TOOLTIP}>
      <span className={`text-[11px] ${version.kind === 'reviewed' ? 'text-gray-500 dark:text-gray-400' : 'text-amber-600 dark:text-amber-400'}`}>
        {version.kind === 'reviewed' ? 'Reviewed' : 'Changed'}
      </span>
      <span className="text-[11px] font-mono text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full px-1.5 py-0.5">
        {version.hash}
      </span>
    </span>
  );
};

// Flatten prepared cycles into a single ordered list of one-line events,
// newest first so the latest activity is visible without scrolling (FECOA-1599).
const toEvents = (prepared) => {
  const events = [];
  prepared.forEach(({ r, decided, decisionMeta, version, standalone }) => {
    // Standalone actions (publish, revoke) are a single event by one actor —
    // no preceding "Submitted for review" row (FECOA-1694).
    if (standalone) {
      const actor = deciderOf(r);
      events.push({
        key: `${r.id}-action`,
        meta: decisionMeta,
        name: actor.name,
        email: actor.email,
        at: r.decided_at || r.submitted_at,
        hasReason: !!r.decision_reason,
        signed: r.signed_by_name || null,
      });
      return;
    }
    events.push({
      key: `${r.id}-submit`,
      meta: SUBMIT_META,
      name: r.submitted_by_name || 'Unknown',
      email: r.submitted_by_email,
      at: r.submitted_at,
      version,
    });
    if (decided) {
      const decider = deciderOf(r);
      events.push({
        key: `${r.id}-decision`,
        meta: decisionMeta,
        name: decider.name,
        email: decider.email,
        at: r.decided_at,
        hasReason: !!r.decision_reason,
        signed: r.signed_by_name || null,
      });
    } else {
      events.push({
        key: `${r.id}-awaiting`,
        meta: decisionMeta,
        awaiting: true,
      });
    }
  });
  return events.reverse();
};

// The one-line-per-event table, shared by the modal and the embeddable inline
// panel so both render identically (FECOA-1741).
const ReviewHistoryTable = ({ events }) => (
  <table className="w-full text-sm border-collapse">
    <thead>
      <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700">
        <th className="font-medium py-2 pr-3">Event</th>
        <th className="font-medium py-2 pr-3">By</th>
        <th className="font-medium py-2 pr-3">Details</th>
        <th className="font-medium py-2 whitespace-nowrap">When</th>
      </tr>
    </thead>
    <tbody>
      {events.map((e) => (
        <tr key={e.key} className="border-b border-gray-50 dark:border-gray-700/50 align-middle">
          <td className="py-2.5 pr-3">
            <EventPill meta={e.meta} />
          </td>
          <td className="py-2.5 pr-3 text-gray-700 dark:text-gray-300">
            {e.awaiting ? (
              <span className="text-gray-400 dark:text-gray-500 italic">—</span>
            ) : (
              <span className="whitespace-nowrap">
                <span className="font-medium">{e.name}</span>
                {e.email && e.email !== e.name && (
                  <span className="text-gray-400"> ({e.email})</span>
                )}
              </span>
            )}
          </td>
          <td className="py-2.5 pr-3">
            {e.awaiting ? (
              <span className="text-[11px] text-gray-400 dark:text-gray-500 italic">Awaiting decision</span>
            ) : e.version ? (
              versionCell(e.version)
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-400">
                {e.hasReason && (
                  <span className="inline-flex items-center gap-1 text-[11px]" title="A decision reason was recorded — see the full audit history.">
                    <MessageSquare className="w-3.5 h-3.5" /> Reason
                  </span>
                )}
                {e.signed && (
                  <span className="inline-flex items-center gap-1 text-[11px]" title="This decision was e-signed — see the full audit history.">
                    <FileSignature className="w-3.5 h-3.5" /> Signed
                  </span>
                )}
              </span>
            )}
          </td>
          <td className="py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {e.awaiting ? '—' : fmt(e.at)}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Shared data-loading hook body: fetch the raw review rows for an entity.
const useReviewHistoryRows = (entityType, objectId) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getReviewHistory(entityType, objectId);
        if (active) setRows(Array.isArray(data) ? data : data.results || []);
      } catch {
        if (active) setError('Could not load review history.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [entityType, objectId]);
  return { rows, loading, error };
};

// Chrome-less, embeddable review history: the same event table without the modal
// overlay/header/footer. Used in the Manage Assessment "Version History" tab so a
// reviewing admin sees the submission / denial / approval trail there, matching
// the card's audit history (FECOA-1741).
export const ReviewHistoryInline = ({ entityType = 'assessment', objectId }) => {
  const { rows, loading, error } = useReviewHistoryRows(entityType, objectId);
  const events = toEvents(prepareCycles(rows));

  if (loading) return <div className="text-center py-6 text-gray-400 text-sm">Loading review history…</div>;
  if (error) return <div className="text-sm text-red-600 py-3">{error}</div>;
  if (events.length === 0) {
    return <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">No review activity yet for this assessment.</div>;
  }
  return <ReviewHistoryTable events={events} />;
};

const ReviewHistory = ({ entityType = 'assessment', objectId, entityName, currentStatus, onClose }) => {
  const navigate = useNavigate();
  const { rows, loading, error } = useReviewHistoryRows(entityType, objectId);

  const events = toEvents(prepareCycles(rows));
  const statusMeta = currentStatus ? CURRENT_STATUS_META[currentStatus] : null;

  const openFullPage = () => {
    onClose();
    navigate(`/review-history/${entityType}/${objectId}`, {
      state: { entityName, currentStatus },
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-[10000] p-4 text-left whitespace-normal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review history</h3>
            {entityName && <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{entityName}</p>}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {statusMeta && (
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusMeta.cls}`}>
                {statusMeta.label}
              </span>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body — scrolls vertically; each event is one row */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading && <div className="text-center py-10 text-gray-400 text-sm">Loading history…</div>}
          {error && <div className="text-sm text-red-600 py-4">{error}</div>}
          {!loading && !error && events.length === 0 && (
            <div className="text-center py-10 text-gray-500 text-sm">
              No review activity yet for this item.
            </div>
          )}

          {!loading && events.length > 0 && <ReviewHistoryTable events={events} />}
        </div>

        {/* Footer — link to the full audit page */}
        {!loading && !error && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button
              onClick={openFullPage}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              <span>View full audit history</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewHistory;
